// ProfileCreateStatus.jsx
//
// Route: /create-space-success
//
// Confirms what was created, then lets the user rename each space or
// swap its picture before going to their home. Every space arrives with
// a working default, so doing nothing is a valid choice.

import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "../CreateProfile/CreateProfile.css";
import kleidologo from "../../../../assets/images/Kleido.png";

/* ─────────────────────────────────────────────────────────
   DEFAULTS
───────────────────────────────────────────────────────── */

/** a default glyph and colour per space type, so nothing ships empty */
const SPACE_DEFAULTS = {
  Drama:   { emoji: "🎭", tint: "#8b5cf6" },
  Music:   { emoji: "🎵", tint: "#4fd1c5" },
  Speaker: { emoji: "🎤", tint: "#f0a274" },
  Food:    { emoji: "🍜", tint: "#f0c674" },
  Sports:  { emoji: "🏅", tint: "#6ee7c8" },
  Decor:   { emoji: "🪴", tint: "#a3d977" },
  Fashion: { emoji: "👗", tint: "#e06a9c" },
  Dance:   { emoji: "💃", tint: "#e06a6a" },
  Travel:  { emoji: "✈️", tint: "#5fa8f0" },
  Biking:  { emoji: "🚴", tint: "#f08a4b" },
  Hiking:  { emoji: "🥾", tint: "#a3866a" },
};

const FALLBACK = { emoji: "✨", tint: "#4fd1c5" };

const defaultsFor = (type) => SPACE_DEFAULTS[type] || FALLBACK;

/* ─────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────── */

const ProfileCreateStatus = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [userId, setUserId] = useState("");
  const [profiles, setProfiles] = useState([]);
  const [editingName, setEditingName] = useState(null);
  const [saved, setSaved] = useState(false);

  /** one hidden file input, retargeted per card */
  const fileRef = useRef(null);
  const pendingType = useRef(null);

  /* ── load what was just created ─────────────────────── */

  useEffect(() => {
    // router state first, then ?userId=, then the stored fallback
    const id =
      location.state?.userId ||
      searchParams.get("userId") ||
      localStorage.getItem("lastCreatedUserId") ||
      "";

    setUserId(id);

    try {
      const raw = localStorage.getItem("profiles");
      const parsed = raw ? JSON.parse(raw) : [];
      const all = Array.isArray(parsed) ? parsed : [];
      setProfiles(id ? all.filter((p) => p.userId === id) : all);
    } catch (err) {
      console.error("Could not read profiles:", err);
      setProfiles([]);
    }
  }, [location.state, searchParams]);

  /* ── edits ──────────────────────────────────────────── */

  const updateProfile = (profileType, patch) => {
    setProfiles((prev) =>
      prev.map((p) => (p.profileType === profileType ? { ...p, ...patch } : p)),
    );
    setSaved(false);
  };

  const pickImage = (profileType) => {
    pendingType.current = profileType;
    if (fileRef.current) fileRef.current.click();
  };

  const handleFile = (e) => {
    const file = e.target.files && e.target.files[0];
    const type = pendingType.current;
    if (!file || !type) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        updateProfile(type, { profileImage: reader.result });
      }
    };
    reader.readAsDataURL(file);

    // let the same file be chosen again later
    e.target.value = "";
    pendingType.current = null;
  };

  const resetImage = (profileType) =>
    updateProfile(profileType, { profileImage: undefined });

  /* ── save and go home ───────────────────────────────── */

  const persist = () => {
    try {
      const raw = localStorage.getItem("profiles");
      const parsed = raw ? JSON.parse(raw) : [];
      const all = Array.isArray(parsed) ? parsed : [];

      const merged = [
        ...all.filter(
          (p) => !profiles.some((n) => n.userId === p.userId && n.profileType === p.profileType),
        ),
        ...profiles,
      ];

      localStorage.setItem("profiles", JSON.stringify(merged));
      return true;
    } catch (err) {
      console.error("Failed to store profiles:", err);
      return false;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    persist();
    setSaved(true);
    navigate(`/user-feed/${userId}`);
  };

  const handleSkip = () => {
    navigate(`/user-feed/${userId}`);
  };

  /* ── render ─────────────────────────────────────────── */

  if (profiles.length === 0) {
    return (
      <div className="cp-auth-container">
        <div className="pc-form-container">
          <div className="form-box-header">
            <h2>No spaces yet</h2>
            <img src={kleidologo} alt="Kleido" className="logo-default-container-img" />
          </div>
          <p className="pc-hint">
            Nothing has been created under this account.{" "}
            <Link to="/create-profile">Create your spaces</Link>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-auth-container">
      <div className="pc-form-container cp-status-panel" style={{background: "black"}}>
        <div className="form-box-header">
          <h2 style={{color: "white"}}>Spaces Created Successfully</h2>
          <img src={kleidologo} alt="Kleido" className="logo-default-container-img" />
        </div>

        <div className="cp-success">
          <span className="cp-success-tick">✓</span>
          <div>
            <p className="cp-success-title">
              {profiles.length} space{profiles.length === 1 ? "" : "s"} created for{" "}
              <strong>{userId}</strong>
            </p>
            <p className="cp-success-sub">
              {profiles.map((p) => p.profileName).join(", ")}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <p className="pc-hint cp-edit-note">
            Each space has a name and a picture already. Change them now if you want to —
            or leave them and carry on.
          </p>

          <div className="cp-space-grid">
            {profiles.map((profile) => {
              const d = defaultsFor(profile.profileType);
              const isEditing = editingName === profile.profileType;
              const duplicate =
                profile.profileName.trim() !== "" &&
                profiles.filter(
                  (p) =>
                    p.profileName.trim().toLowerCase() ===
                    profile.profileName.trim().toLowerCase(),
                ).length > 1;

              return (
                <div key={profile.profileType} className="cp-space-card">
                  <button
                    type="button"
                    className="cp-space-image"
                    style={{
                      borderColor: `${d.tint}66`,
                      background: profile.profileImage ? "transparent" : `${d.tint}1a`,
                    }}
                    onClick={() => pickImage(profile.profileType)}
                    aria-label={`Change picture for ${profile.profileName}`}
                  >
                    {profile.profileImage ? (
                      <img src={profile.profileImage} alt={profile.profileName} />
                    ) : (
                      <span className="cp-space-emoji">{d.emoji}</span>
                    )}
                    <span className="cp-space-image-edit">Change</span>
                  </button>

                  <span className="cp-space-type" style={{ color: d.tint }}>
                    {profile.profileType}
                  </span>

                  {isEditing ? (
                    <input
                      className="cp-space-name-input"
                      value={profile.profileName}
                      autoFocus
                      onChange={(e) =>
                        updateProfile(profile.profileType, { profileName: e.target.value })
                      }
                      onBlur={() => {
                        if (!profile.profileName.trim()) {
                          updateProfile(profile.profileType, {
                            profileName: `${userId}_${profile.profileType}`,
                          });
                        }
                        setEditingName(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          setEditingName(null);
                        }
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="cp-space-name"
                      onClick={() => setEditingName(profile.profileType)}
                      title="Rename this space"
                    >
                      {profile.profileName}
                      <span className="cp-space-pencil">✎</span>
                    </button>
                  )}

                  {duplicate && (
                    <span className="cp-space-warn">Two spaces share this name</span>
                  )}

                  {profile.profileImage && (
                    <button
                      type="button"
                      className="cp-space-reset"
                      onClick={() => resetImage(profile.profileType)}
                    >
                      Use default picture
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* one input serves every card */}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFile}
          />

          {saved && <p className="cp-saved-note">Changes saved.</p>}

          <div className="cp-status-actions">
            <button type="button" className="pc-btn" onClick={handleSkip}>
              Keep defaults
            </button>
            <button type="submit" className="submit-btn cp-status-submit">
              Save and continue
            </button>
          </div>

          <div className="copyright-container">
            <p className="copyright">
              Kleido © InitiumNet Technology Pvt Ltd. For more on privacy policies and
              cookies, click <Link to="/">here</Link>.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileCreateStatus;